import type { MutableRefObject } from 'react'
import { useEffect } from 'react'
import { getPublicEnrollmentStateRequest } from '@/api/enrollment-api'
import { useEnrollmentStore } from '@/stores'
import { getEnrollmentApiErrorMessage } from '../../../utils/enrollmentApiErrorMessage'
import type { ApplyHydrationFn } from '../types'
import { applyHydratedSignatureState } from './sessionHydration/applyHydratedSignatureState'
import { resolveSessionStepSyncPolicy } from './sessionHydration/stepSyncPolicy'

type UseSessionHydrationEffectArgs = {
  currentStep: number
  initialStep: number | null
  enrollmentSessionToken: string | null
  applyHydration: ApplyHydrationFn
  shouldSettleInitialHydration: boolean
  onInitialHydrationSettled: () => void
  suppressNextSessionStepSyncRef: MutableRefObject<boolean>
  sessionStepSyncLockRef: MutableRefObject<number | null>
  setSignatureStatus: (value: string | null) => void
  setIsSignatureSigned: (value: boolean) => void
  setSignatureCompleted: (completed: boolean) => void
  setStepValidationError: (value: string | null) => void
}

export function useSessionHydrationEffect({
  currentStep,
  initialStep,
  enrollmentSessionToken,
  applyHydration,
  shouldSettleInitialHydration,
  onInitialHydrationSettled,
  suppressNextSessionStepSyncRef,
  sessionStepSyncLockRef,
  setSignatureStatus,
  setIsSignatureSigned,
  setSignatureCompleted,
  setStepValidationError
}: UseSessionHydrationEffectArgs) {
  const setResumedEnrollment = useEnrollmentStore(state => state.setResumedEnrollment)

  useEffect(() => {
    if (!enrollmentSessionToken) {
      return
    }

    let mounted = true

    const hydrateSession = async () => {
      try {
        const state = await getPublicEnrollmentStateRequest(enrollmentSessionToken)
        if (!mounted) {
          return
        }

        setResumedEnrollment(state)
        const { shouldSyncStep } = resolveSessionStepSyncPolicy({
          currentStep,
          suppressNextSessionStepSyncRef,
          sessionStepSyncLockRef
        })
        const shouldApplyRemoteStep = initialStep === null && shouldSyncStep

        applyHydration({
          enrollment: state,
          syncStep: shouldApplyRemoteStep,
          stepSyncMode: 'remote'
        })
        applyHydratedSignatureState({
          state,
          setSignatureStatus,
          setIsSignatureSigned,
          setSignatureCompleted
        })
        if (shouldSettleInitialHydration) {
          onInitialHydrationSettled()
        }
      } catch (error) {
        if (!mounted) {
          return
        }

        if (shouldSettleInitialHydration) {
          onInitialHydrationSettled()
        }
        setStepValidationError(
          getEnrollmentApiErrorMessage(
            error,
            'We could not refresh your enrollment progress. Please return to the application and try again.'
          )
        )
      }
    }

    void hydrateSession()

    return () => {
      mounted = false
    }
  }, [
    applyHydration,
    currentStep,
    enrollmentSessionToken,
    initialStep,
    onInitialHydrationSettled,
    setIsSignatureSigned,
    setSignatureCompleted,
    setResumedEnrollment,
    setSignatureStatus,
    setStepValidationError,
    sessionStepSyncLockRef,
    shouldSettleInitialHydration,
    suppressNextSessionStepSyncRef
  ])
}
